import React from 'react';
import { CheckCircle2, Clock, Building2, User, Mail, Phone, Hash, Shield, MapPin, Globe, Calendar, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { HighTechCard } from './shared';
import { useNavigate } from 'react-router-dom';
import { formatCodeNAF } from '../../utils/codeNaf';
import { getDashboardRouteForRole } from '../../utils/roleNavigation';
import { useAuth } from '../../contexts/AuthContext';
import SepaMandateBlock from './SepaMandateBlock';

export default function StepConfirmation({ ctx }) {
  const { formData, montantTTC, souscriptionCreee, paiementEchelonne, echelonnementOverride } = ctx;
  const navigate = useNavigate();
  const { user } = useAuth();
  const sousId = souscriptionCreee?.id || souscriptionCreee?.numero_police;
  // V36.85 — Tableau de bord cohérent avec le rôle connecté (un super_admin
  // ne doit pas être renvoyé vers /cooper/dashboard).
  const dashboardRoute = getDashboardRouteForRole(user?.role);

  return (
    <HighTechCard gradient="green">
      <div className="p-1">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur animate-pulse"><CheckCircle2 className="h-6 w-6 text-white" /></div>
            <div><h2 className="text-xl font-bold text-white">La souscription a bien été enregistrée</h2><p className="text-green-100 text-sm">Dossier en cours de validation</p></div>
          </div>
        </div>
        <div className="p-6 space-y-6 bg-white rounded-b-xl">
          <div className="p-5 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="confirmation-message-validation">
            <p className="font-bold text-amber-900">La souscription a bien été enregistrée.</p>
            <p className="mt-1 text-sm text-amber-800">Dès validation de la souscription, les documents (attestations + CP/CG, reçus) seront disponibles dans votre ESPACE membre ou COOPER&apos;S. D&apos;ici là, le dossier s&apos;affiche avec la mention « En cours de validation » et les documents sont consultables uniquement, sans téléchargement ni impression.</p>
          </div>
          <div className="p-5 rounded-xl bg-green-50 border border-green-200">
            <h4 className="font-bold text-green-800 mb-4">Recapitulatif</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Ref. Solidaire</span><span className="font-mono font-bold text-green-700">{souscriptionCreee?.numero_police || souscriptionCreee?.id || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Montant Global</span><span className="font-bold text-green-700" data-testid="confirmation-montant-global">{montantTTC?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Statut</span><Badge className="bg-amber-500"><Clock className="h-3 w-3 mr-1" />En cours de validation</Badge></div>
            </div>
          </div>
          {/* Coordonnees du membre */}
          <div className="p-5 rounded-xl bg-slate-50 border border-slate-200" data-testid="confirmation-coordonnees">
            <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" />Coordonnees du membre</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Entreprise :</span><span className="font-semibold">{formData.raison_sociale}</span></div>
              {formData.forme_juridique && <div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Statut :</span><span>{formData.forme_juridique}</span></div>}
              {formData.siret && <div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">SIRET :</span><span className="font-mono text-xs">{formData.siret}</span></div>}
              {formData.code_naf && <div className="flex items-center gap-2"><Hash className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Code APE :</span><span className="font-mono text-xs">{formatCodeNAF(formData.code_naf)}</span></div>}
              {(formData.nom_gerant || formData.prenom_gerant) && <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Gerant :</span><span className="font-semibold">{formData.prenom_gerant} {formData.nom_gerant}</span></div>}
              {formData.adresse_entreprise && <div className="flex items-center gap-2 col-span-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Adresse :</span><span>{formData.adresse_entreprise}{formData.code_postal ? `, ${formData.code_postal}` : ''} {formData.ville || ''}</span></div>}
              {(() => {
                const territoiresArr = Array.isArray(formData.territoires) && formData.territoires.length > 0
                  ? formData.territoires
                  : (formData.territoire ? [formData.territoire] : []);
                if (territoiresArr.length === 0) return null;
                if (territoiresArr.length === 1) {
                  return (
                    <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Territoire :</span><span>{formData.territoire || territoiresArr[0]}</span></div>
                  );
                }
                const tca = formData.territoires_ca || {};
                return (
                  <div className="col-span-2 flex items-start gap-2">
                    <Globe className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                    <span className="text-slate-500">Territoires&nbsp;:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {territoiresArr.map((id) => {
                        const ca = tca[id];
                        return (
                          <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-900 text-xs">
                            <span className="font-medium">{id}</span>
                            {ca ? <span className="text-blue-700">— {Number(ca).toLocaleString('fr-FR')} € HT</span> : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {formData.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Email :</span><span>{formData.email}</span></div>}
              {formData.telephone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Tel :</span><span>{formData.telephone}</span></div>}
              {formData.site_web && <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Site :</span><span>{formData.site_web}</span></div>}
              {formData.date_creation_entreprise && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Creation :</span><span>{formData.date_creation_entreprise}</span></div>}
              {formData.effectif && <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-slate-400" /><span className="text-slate-500">Effectif :</span><span>{formData.effectif}</span></div>}
            </div>
          </div>
          {/* === Mandat SEPA pour les 40% restants (V35.32) ===
              V36.52 — Ne s'affiche QUE si le membre a choisi le paiement
              échelonné (60% virement + 40% SEPA). En paiement intégral
              par virement, le bloc SEPA est inutile. */}
          {paiementEchelonne && sousId && montantTTC > 0 && (
            <SepaMandateBlock
              souscriptionId={sousId}
              montantTotalTTC={montantTTC}
              formData={formData}
              referenceSolidaire={souscriptionCreee?.numero_police || souscriptionCreee?.id}
              echelonnementOverride={echelonnementOverride}
            />
          )}
          <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-4">Prochaines etapes</h4>
            <div className="space-y-3">
              {[
                { num: 1, title: 'Validation paiement', desc: 'Super Admin valide sous 24-48h' },
                { num: 2, title: 'Generation documents', desc: 'Attestation + Recu automatiques' },
                { num: 3, title: 'Documents disponibles', desc: 'Dans l\'espace membre' }
              ].map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">{step.num}</span>
                  <div><p className="font-semibold text-blue-800">{step.title}</p><p className="text-sm text-blue-600">{step.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate(dashboardRoute)} variant="outline" className="flex-1 h-12" data-testid="confirmation-dashboard-btn">Tableau de bord</Button>
            <Button onClick={() => navigate('/calculateur-batico')} className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600">Nouvelle souscription</Button>
          </div>
        </div>
      </div>
    </HighTechCard>
  );
}
